import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { addImpulse } from './prices';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Foreign Asset arc — late-game spy thriller.
 *
 * Triggered when foreign-intel contact present + FBI heat >= 30 +
 * cash >= $1M. The foreign service "activates" you. 4 ops decisions
 * decide whether you walk away clean ($800K + permanent FBI heat -10)
 * or get burned (FBI raid cinematic, no extraction).
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => number }[]; // tradecraft score delta
}

const BEATS: Beat[] = [
  {
    text: "[unknown number]: \"we have a small request. \$200K to a numbered account. you don\'t need to know what for.\"",
    options: [
      { label: 'transfer the \$200K. don\'t ask.', effect: () => {
        useMarkets.getState().addCash(-200_000);
        return 2;
      } },
      { label: 'ask what for.', effect: () => {
        useLife.getState().bumpFbiHeat(8);
        return -1;
      } },
      { label: 'ignore the message.', effect: () => {
        useLife.getState().bumpSanity(-3);
        return -2;
      } },
    ],
  },
  {
    text: "[unknown number]: \"we need you to retweet this account 4 times this week. it\'s a meme account.\"",
    options: [
      { label: 'do it. retweet on schedule.', effect: () => {
        useXClone.getState().composePost("interesting account everyone should be following: @[redacted].");
        return 2;
      } },
      { label: 'do it but caveat: "engaging not endorsing"', effect: () => {
        usePower.getState().bumpRep('press', 4);
        return 1;
      } },
      { label: 'refuse. block them.', effect: () => {
        useLife.getState().bumpFbiHeat(-4);
        return -2;
      } },
    ],
  },
  {
    text: "[unknown number]: \"trade these tickers in this exact sequence. tomorrow at 9:42am. why: classified.\"",
    options: [
      { label: 'follow the sequence', effect: () => {
        addImpulse({ ticker: 'TARI', startMs: Date.now() + 60_000, durationMs: 12 * 60_000, logDelta: 0.06 });
        addImpulse({ ticker: 'CHIP', startMs: Date.now() + 60_000, durationMs: 12 * 60_000, logDelta: 0.04 });
        useLife.getState().bumpSecHeat(12);
        return 2;
      } },
      { label: 'follow but with smaller size', effect: () => {
        useLife.getState().bumpSecHeat(6);
        return 1;
      } },
      { label: 'forward the message to the FBI tip line', effect: () => {
        useLife.getState().bumpFbiHeat(-15);
        usePower.getState().bumpRep('press', 6);
        return -3;
      } },
    ],
  },
  {
    text: "[unknown number]: \"final ask. one phone call to a specific Senator. 14 minutes. you say words we provide. \$2M after. we burn the line after.\"",
    options: [
      { label: 'make the call. take the money.', effect: () => {
        useMarkets.getState().addCash(2_000_000);
        useLife.getState().bumpFbiHeat(15);
        return 3;
      } },
      { label: 'demand more. \$5M.', effect: () => {
        useMarkets.getState().addCash(5_000_000);
        useLife.getState().bumpFbiHeat(25);
        usePower.getState().bumpRep('press', -10);
        return 2;
      } },
      { label: 'walk. burn your phone. fly to Wyoming.', effect: () => {
        useLife.getState().bumpFbiHeat(-20);
        useLife.getState().bumpSanity(-8);
        return -1;
      } },
    ],
  },
];

let tradecraftScore = 0;

export function startForeignAssetArc(): void {
  tradecraftScore = 0;
  useMeta.getState().recordLore('arc-foreign-asset', 'Foreign Asset');
  useCinematic.getState().fire({
    id: `foreign-asset-${Date.now()}`,
    type: 'breaking',
    headline: 'YOU\'VE BEEN ACTIVATED.',
    subhead: 'the [unknown number] is calling in their favors. 4 ops decisions. one of them involves a Senator. one of them involves \$2M cash. neither involves the FBI knowing.',
    durationMs: 6500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `asset-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `asset-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          tradecraftScore += opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'foreign-intel',
        fromNpc: 'foreign-intel',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `[unknown]: op ${i + 1}/4` });
      sounds.notifyAlert();
    }, 6_000 + i * 35_000);
  }

  setTimeout(() => {
    if (tradecraftScore >= 7) {
      useCinematic.getState().fire({
        id: `asset-clean-${Date.now()}`,
        type: 'ascension',
        headline: 'EXTRACTED CLEAN.',
        subhead: 'the line is dead. the money is in. nobody is looking. you can never speak of this.',
        durationMs: 7000,
      });
      useMarkets.getState().addCash(800_000);
      useLife.getState().bumpFbiHeat(-10);
    } else if (tradecraftScore <= 0) {
      useCinematic.getState().fire({
        id: `asset-burned-${Date.now()}`,
        type: 'breaking',
        headline: 'YOU\'VE BEEN BURNED.',
        subhead: 'the FBI knows. the foreign service knows the FBI knows. you have, in the last 4 hours, gone from "useful" to "liability." Wyoming flight booked.',
        durationMs: 7000,
      });
      useLife.getState().bumpFbiHeat(40);
      useLife.getState().bumpSanity(-15);
    } else {
      useNotifications.getState().push({
        surfaceId: 'phone', severity: 'info',
        text: '[unknown]: "we\'ll be in touch."',
      });
    }
  }, 6_000 + BEATS.length * 35_000 + 5_000);
}
